import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ReplayModal from '../../components/ReplayModal';
import PriceChart from '../../components/PriceChart';
import Button from '../../components/Button';
import Skeleton from '../../components/Skeleton';
import { detectBarInterval } from './utils';

export default function PriceChartSection({ backtest, onCrosshairMove }) {
  const navigate = useNavigate();
  const [showReplay, setShowReplay] = useState(false);

  // Multi-asset: price_series_map holds one OHLC series per symbol. When present
  // with >1 symbol, show a switcher; otherwise fall back to the legacy single
  // price_series (the primary symbol).
  const priceMap = backtest?.price_series_map || {};
  const mapSymbols = Object.keys(priceMap);
  const isMulti = mapSymbols.length > 1;
  const [selectedSymbol, setSelectedSymbol] = useState(backtest?.symbol);
  const activeSymbol = isMulti ? (mapSymbols.includes(selectedSymbol) ? selectedSymbol : mapSymbols[0]) : backtest?.symbol;

  const activePriceSeries = useMemo(() => {
    if (!backtest) return [];
    if (isMulti) return priceMap[activeSymbol] || [];
    return backtest.price_series || [];
  }, [backtest, isMulti, priceMap, activeSymbol]);

  // Only markers for the symbol currently shown. Legacy single-symbol runs have
  // no `symbol` on their events, so show them all in that case.
  const tradeEvents = useMemo(() => {
    const all = backtest?.trade_events || [];
    if (!isMulti) return all;
    return all.filter((ev) => ev.symbol === activeSymbol);
  }, [backtest, isMulti, activeSymbol]);

  const priceChartData = useMemo(() => {
    if (!backtest) return [];
    const ps = activePriceSeries;
    const is = backtest.indicator_series || {};
    const inames = Object.keys(is);
    const iByDate = {};
    inames.forEach((name) => {
      iByDate[name] = {};
      (is[name] || []).forEach(({ date, value }) => { iByDate[name][date] = value; });
    });
    return ps.map((d) => {
      const row = { ...d, candleRange: [d.low, d.high] };
      inames.forEach((name) => {
        const val = iByDate[name]?.[d.date];
        if (val !== undefined) row[name] = val;
      });
      return row;
    });
  }, [backtest, activePriceSeries]);

  // While a backtest is still running/pending there are no series yet — show a
  // skeleton sized like the chart so the page doesn't collapse or jump.
  if (priceChartData.length === 0) {
    if (backtest && (backtest.status === 'running' || backtest.status === 'pending')) {
      return (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Skeleton width={140} height={20} style={{ marginBottom: 'var(--space-2)' }} />
          <Skeleton height={300} />
        </div>
      );
    }
    return null;
  }

  const isFx = backtest.asset_type === 'fx';
  // On a multi-asset chart, show only the active symbol's indicators (plus any
  // whole-market/unmapped overlays); on single-symbol runs, show them all.
  const indicatorSymbols = backtest.results?.indicator_symbols || {};
  const indicatorNames = Object.keys(backtest.indicator_series || {}).filter(
    (name) => !isMulti || !indicatorSymbols[name] || indicatorSymbols[name] === activeSymbol
  );
  const equityCurve = backtest.equity_curve || [];
  const priceByDate = {};
  activePriceSeries.forEach((d) => { priceByDate[d.date] = d; });

  // Show a note when the chart display interval differs from the strategy's native interval.
  const nativeInterval = backtest.fx_interval || '1d';
  const displayInterval = detectBarInterval(priceChartData);
  const chartIsDownsampled = displayInterval != null && displayInterval !== nativeInterval;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-2)', flexWrap: 'wrap' }}>
        <h3 className="section-title" style={{ margin: 0 }}>
          {(backtest.asset_type === 'stock' || backtest.asset_type === 'fx') ? (
            <span
              onClick={() => navigate(`/stock/${activeSymbol}${backtest.asset_type === 'fx' ? '?asset_type=fx' : ''}`)}
              style={{ cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'var(--border)' }}
              title={`View ${activeSymbol} detail page`}
            >
              {activeSymbol}
            </span>
          ) : activeSymbol} Price
        </h3>
        <Button variant="secondary" size="sm" onClick={() => setShowReplay(true)}>▶ Replay</Button>
      </div>
      {isMulti && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 'var(--space-2)' }}>
          {mapSymbols.map((sym) => (
            <button
              key={sym}
              onClick={() => setSelectedSymbol(sym)}
              style={{
                padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                borderRadius: 6, border: '1px solid var(--border)',
                background: sym === activeSymbol ? 'var(--accent, #6366f1)' : 'var(--card)',
                color: sym === activeSymbol ? '#fff' : 'var(--text)',
              }}
            >
              {sym}{sym === backtest.symbol ? ' ◆' : ''}
            </button>
          ))}
        </div>
      )}
      {chartIsDownsampled && (
        <p style={{
          margin: '0 0 8px', fontSize: 11, color: 'var(--muted)',
          fontStyle: 'italic',
        }}>
          Strategy ran at {nativeInterval} bars &mdash; chart is displaying at {displayInterval} bars for performance.
        </p>
      )}
      <PriceChart
        key={`${backtest.id}:${activeSymbol}`}
        data={priceChartData}
        indicatorNames={indicatorNames}
        tradeEvents={tradeEvents}
        isFx={isFx}
        onCrosshairMove={onCrosshairMove}
      />

      {showReplay && (
        <ReplayModal
          priceChartData={priceChartData}
          tradeEvents={tradeEvents}
          indicatorNames={indicatorNames}
          priceByDate={priceByDate}
          equityCurve={equityCurve}
          symbol={activeSymbol}
          isFx={isFx}
          onClose={() => setShowReplay(false)}
        />
      )}
    </>
  );
}
